/**
 * Container child selection management hook
 *
 * Features:
 * - Independent child selection within containers
 * - Maintains container selection hierarchy
 * - Multi-child selection support (Shift+click)
 * - Keyboard navigation between children
 * - Context-aware properties panel switching
 */

import { useCallback, useState, useMemo, useEffect } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { Component } from '@/types';

export interface ChildSelectionState {
  /** Currently selected children within this container */
  selectedChildren: string[];
  /** Child that has keyboard focus */
  focusedChildId: string | null;
  /** Whether container itself is selected */
  containerSelected: boolean;
  /** Selection mode: 'single' | 'multi' */
  selectionMode: 'single' | 'multi';
}

export interface UseContainerChildSelectionOptions {
  containerId: string;
  /** Allow multi-selection with Shift+click */
  enableMultiSelect?: boolean;
  /** Auto-focus first child when container is selected */
  autoFocusFirstChild?: boolean;
  /** Callbacks */
  onChildSelect?: (childId: string, selected: boolean) => void;
  onChildFocus?: (childId: string | null) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
}

export interface UseContainerChildSelectionReturn {
  selectionState: ChildSelectionState;

  // Selection methods
  selectChild: (childId: string, addToSelection?: boolean) => void;
  deselectChild: (childId: string) => void;
  selectChildren: (childIds: string[]) => void;
  clearChildSelection: () => void;
  toggleChildSelection: (childId: string) => void;
  selectAllChildren: () => void;

  // Focus methods
  focusChild: (childId: string | null) => void;
  focusNextChild: () => void;
  focusPreviousChild: () => void;
  focusFirstChild: () => void;
  focusLastChild: () => void;

  // Query methods
  isChildSelected: (childId: string) => boolean;
  isChildFocused: (childId: string) => boolean;
  getSelectedChildren: () => Component[];
  getFocusedChild: () => Component | null;
  hasChildSelection: () => boolean;

  // Event handlers for components
  createChildClickHandler: (childId: string) => (event: React.MouseEvent) => void;
  createChildKeydownHandler: (childId: string) => (event: React.KeyboardEvent) => void;
}

/**
 * Hook for managing child selection within FlexContainers
 */
export function useContainerChildSelection({
  containerId,
  enableMultiSelect = true,
  autoFocusFirstChild = false,
  onChildSelect,
  onChildFocus,
  onSelectionChange
}: UseContainerChildSelectionOptions): UseContainerChildSelectionReturn {

  // Editor store hooks
  const getComponent = useEditorStore(state => state.getComponent);
  const getComponentChildren = useEditorStore(state => state.getComponentChildren);
  const isComponentSelected = useEditorStore(state => state.isComponentSelected);
  const selectComponent = useEditorStore(state => state.selectComponent);
  const clearSelection = useEditorStore(state => state.clearSelection);
  const toggleComponentSelection = useEditorStore(state => state.toggleComponentSelection);

  // Local selection state (independent of global selection)
  const [selectedChildren, setSelectedChildren] = useState<string[]>([]);
  const [focusedChildId, setFocusedChildId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState<'single' | 'multi'>('single');

  // Get current children and container state
  const children = useMemo(() => getComponentChildren(containerId), [getComponentChildren, containerId]);
  const containerSelected = useMemo(() => isComponentSelected(containerId), [isComponentSelected, containerId]);

  // Build selection state
  const selectionState: ChildSelectionState = useMemo(() => ({
    selectedChildren,
    focusedChildId,
    containerSelected,
    selectionMode
  }), [selectedChildren, focusedChildId, containerSelected, selectionMode]);

  // Auto-focus first child when container becomes selected
  useEffect(() => {
    if (containerSelected && autoFocusFirstChild && children.length > 0 && !focusedChildId) {
      setFocusedChildId(children[0].id);
    }
  }, [containerSelected, autoFocusFirstChild, children, focusedChildId]);

  // Clear child selection when container is deselected
  useEffect(() => {
    if (!containerSelected && selectedChildren.length > 0) {
      setSelectedChildren([]);
      setFocusedChildId(null);
    }
  }, [containerSelected, selectedChildren.length]);

  // Note: Removed automatic sync with global selection state to prevent infinite loops
  // onSelectionChange is now only called from direct user interactions in selection methods

  // Selection methods
  const selectChild = useCallback((childId: string, addToSelection = false) => {
    if (!children.some(child => child.id === childId)) return;

    setSelectedChildren(prev => {
      const isAlreadySelected = prev.includes(childId);
      if (isAlreadySelected) return prev;

      let newSelection: string[];
      if (addToSelection && enableMultiSelect) {
        newSelection = [...prev, childId];
        setSelectionMode('multi');
      } else {
        newSelection = [childId];
        setSelectionMode('single');
      }

      return newSelection;
    });

    setFocusedChildId(childId);

    // IMPORTANT: Also update global selection so AI chat panel sees it
    if (addToSelection && enableMultiSelect) {
      // For multi-select, use toggle to add to selection
      toggleComponentSelection(childId);
    } else {
      // For single select, clear and select just this component
      selectComponent(childId);
    }

    onChildSelect?.(childId, true);
  }, [children, enableMultiSelect, onChildSelect, selectComponent, toggleComponentSelection]);

  const deselectChild = useCallback((childId: string) => {
    setSelectedChildren(prev => {
      const newSelection = prev.filter(id => id !== childId);
      if (newSelection.length <= 1) {
        setSelectionMode('single');
      }
      return newSelection;
    });

    if (focusedChildId === childId) {
      // Move focus to next selected child or clear
      const remainingSelected = selectedChildren.filter(id => id !== childId);
      setFocusedChildId(remainingSelected.length > 0 ? remainingSelected[0] : null);
    }

    onChildSelect?.(childId, false);
  }, [selectedChildren, focusedChildId, onChildSelect]);

  const selectChildren = useCallback((childIds: string[]) => {
    const validIds = childIds.filter(id => children.some(child => child.id === id));
    setSelectedChildren(validIds);
    setSelectionMode(validIds.length > 1 ? 'multi' : 'single');

    if (validIds.length > 0 && !validIds.includes(focusedChildId || '')) {
      setFocusedChildId(validIds[0]);
    }
  }, [children, focusedChildId]);

  const clearChildSelection = useCallback(() => {
    setSelectedChildren([]);
    setFocusedChildId(null);
    setSelectionMode('single');
  }, []);

  const toggleChildSelection = useCallback((childId: string) => {
    if (selectedChildren.includes(childId)) {
      deselectChild(childId);
    } else {
      selectChild(childId, selectionMode === 'multi');
    }
  }, [selectedChildren, selectionMode, selectChild, deselectChild]);

  const selectAllChildren = useCallback(() => {
    if (children.length === 0) return;

    const allIds = children.map(child => child.id);
    setSelectedChildren(allIds);
    setSelectionMode('multi');
    setFocusedChildId(allIds[0]);
  }, [children]);

  // Focus methods
  const focusChild = useCallback((childId: string | null) => {
    if (childId === null || children.some(child => child.id === childId)) {
      setFocusedChildId(childId);
      onChildFocus?.(childId);
    }
  }, [children, onChildFocus]);

  const focusNextChild = useCallback(() => {
    if (children.length === 0) return;

    if (!focusedChildId) {
      focusChild(children[0].id);
      return;
    }

    const currentIndex = children.findIndex(child => child.id === focusedChildId);
    const nextIndex = (currentIndex + 1) % children.length;
    focusChild(children[nextIndex].id);
  }, [children, focusedChildId, focusChild]);

  const focusPreviousChild = useCallback(() => {
    if (children.length === 0) return;

    if (!focusedChildId) {
      focusChild(children[children.length - 1].id);
      return;
    }

    const currentIndex = children.findIndex(child => child.id === focusedChildId);
    const prevIndex = currentIndex === 0 ? children.length - 1 : currentIndex - 1;
    focusChild(children[prevIndex].id);
  }, [children, focusedChildId, focusChild]);

  const focusFirstChild = useCallback(() => {
    if (children.length > 0) {
      focusChild(children[0].id);
    }
  }, [children, focusChild]);

  const focusLastChild = useCallback(() => {
    if (children.length > 0) {
      focusChild(children[children.length - 1].id);
    }
  }, [children, focusChild]);

  // Query methods
  const isChildSelected = useCallback((childId: string) => {
    return selectedChildren.includes(childId);
  }, [selectedChildren]);

  const isChildFocused = useCallback((childId: string) => {
    return focusedChildId === childId;
  }, [focusedChildId]);

  const getSelectedChildren = useCallback(() => {
    return selectedChildren
      .map(id => children.find(child => child.id === id))
      .filter(Boolean) as Component[];
  }, [selectedChildren, children]);

  const getFocusedChild = useCallback(() => {
    if (!focusedChildId) return null;
    return children.find(child => child.id === focusedChildId) || null;
  }, [focusedChildId, children]);

  const hasChildSelection = useCallback(() => {
    return selectedChildren.length > 0;
  }, [selectedChildren.length]);

  // Event handlers
  const createChildClickHandler = useCallback((childId: string) => {
    return (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const addToSelection = event.shiftKey || event.ctrlKey || event.metaKey;

      if (addToSelection && enableMultiSelect) {
        toggleChildSelection(childId);
      } else {
        selectChild(childId);
      }
    };
  }, [enableMultiSelect, selectChild, toggleChildSelection]);

  const createChildKeydownHandler = useCallback((childId: string) => {
    return (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          selectChild(childId);
          break;

        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          focusNextChild();
          break;

        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          focusPreviousChild();
          break;

        case 'Home':
          event.preventDefault();
          focusFirstChild();
          break;

        case 'End':
          event.preventDefault();
          focusLastChild();
          break;

        case 'a':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            selectAllChildren();
          }
          break;

        case 'Escape':
          event.preventDefault();
          clearChildSelection();
          break;

        default:
          // Let other keys bubble up
          break;
      }
    };
  }, [selectChild, focusNextChild, focusPreviousChild, focusFirstChild, focusLastChild, selectAllChildren, clearChildSelection]);

  return {
    selectionState,
    selectChild,
    deselectChild,
    selectChildren,
    clearChildSelection,
    toggleChildSelection,
    selectAllChildren,
    focusChild,
    focusNextChild,
    focusPreviousChild,
    focusFirstChild,
    focusLastChild,
    isChildSelected,
    isChildFocused,
    getSelectedChildren,
    getFocusedChild,
    hasChildSelection,
    createChildClickHandler,
    createChildKeydownHandler
  };
}
